import { FC, useEffect, useState } from "react";
import { IdTableVariant } from "../../../../../@types/enumsGlobal";
import GridWrapper from "../../../../../components/controls/GridWrapper";
import { NumberInput, TextAreaInput, TextInput } from "../../../../../components/controls/inputs";
import { useLotRemain } from "../../../../../components/lotSelector/useLotRemain";
import useOperativeReserve from "../../../../../components/lotSelector/useOperativeReserve";
import { BaseModalWindow } from "../../../../../components/modalWindows/BaseModalWindow";
import modalStyles from "../../../../../components/modalWindows/styles/BaseModalWindow.module.scss";
import { ILotDTO } from "../../../../../libs/coreapi-dto/accounting/lot";
import { IMovementItemEditDTO } from "../../../../../libs/coreapi-dto/documents/movementItem";
import { ICreateItem } from "../MovementUpdateForm";
import { useForm } from "../../../../../system/hooks/useForm";
import { DateTime } from "luxon";
import { roundMoneyFormat } from "../../../../../system/functions/round";
import { GoodsDataProvider } from "../../../../../Services/DataProviders/GoodsDataProvider";
import { useAppContext } from "../../../../../system/providers/appContextProvider";
import { IGoodsWtruCodeGroupDTO } from "../../../../../libs/coreapi-dto/dirs/goods";
import { useTranslation } from "react-i18next";

interface IMovementUpdateItemModal {
    idDocumentGlobal: string;
    idDocumentItemGlobal: string;
    lot: ILotDTO;
    movementItemEdit: IMovementItemEditDTO;
    kizCount: number;
    ok: (createItem: ICreateItem) => void;
    cancel: () => void;
    requiredQuantity?: number;
    isWithdrawalGismtForUnscannedLots: boolean
}

interface IValidator {
    quantity: number;
}

export const MovementUpdateItemModal: FC<IMovementUpdateItemModal> = (props) => {
    const [createItem, setCreateItem] = useState<IMovementItemEditDTO>(props.movementItemEdit);
    const [actualRequiredQuantity, setActualRequiredQuantity] = useState<number>(1)

    const appCtx = useAppContext();
    const goodsDataProvider = new GoodsDataProvider(appCtx.coreApiService);

    const [kizGoodsGroup, setKizGoodsGroup] = useState<string>("")

    const { t } = useTranslation();
    const baseT = (value: string) => t("documents.movement." + value);

    const isDivided = (props.lot.numerator ?? 1) < (props.lot.denominator ?? 1)
    const isBox = (props.lot.numerator ?? 1) > (props.lot.denominator ?? 1)
    const isKiz = props.lot.isKiz === true

    const buildDocumentItem = (): ICreateItem => {
        return {
            item: {
                ...createItem,
                idGlobal: props.idDocumentItemGlobal,
                isKiz: props.isWithdrawalGismtForUnscannedLots ? true : createItem.isKiz,
                supplierCostInfo: {
                    ...props.lot.supplierCostInfo,
                    vat: props.lot.supplierCostInfo.vat,
                    sum: roundMoneyFormat(props.lot.supplierCostInfo.price * createItem.quantity),
                    vatSum: roundMoneyFormat(props.lot.supplierCostInfo.vatPrice * createItem.quantity),
                    sumIncVat: roundMoneyFormat(props.lot.supplierCostInfo.priceIncVat * createItem.quantity),
                },
                retailCostInfo: {
                    ...props.lot.retailCostInfo,
                    vat: props.lot.retailCostInfo.vat,
                    sum: roundMoneyFormat(props.lot.retailCostInfo.price * createItem.quantity),
                    vatSum: roundMoneyFormat(props.lot.retailCostInfo.vatPrice * createItem.quantity),
                    sumIncVat: roundMoneyFormat(props.lot.retailCostInfo.priceIncVat * createItem.quantity),
                },
                dateCreated: DateTime.now()
            },
            displayItem: {
                idGlobal: props.idDocumentItemGlobal,
                idLotFromGlobal: createItem.idLotFromGlobal,
                goodsName: props.lot.goods.displayName,
                scalingRatioDisplayName: props.lot.scalingRatio.displayName,
                actualQuantity: createItem.actualQuantity,
                quantity: createItem.quantity,
                gosContractName: props.lot.gosContractName,
                goodsCode: props.lot.goodsCode,
                producer: props.lot.producer.displayName,
                supplierVat: props.lot.supplierCostInfo.vat,
                supplierPrice: props.lot.supplierCostInfo.price,
                supplierVatPrice: props.lot.supplierCostInfo.vatPrice,
                supplierPriceIncVat: props.lot.supplierCostInfo.priceIncVat,
                supplierSum: roundMoneyFormat(props.lot.supplierCostInfo.sum * createItem.quantity),
                supplierVatSum: roundMoneyFormat(props.lot.supplierCostInfo.vatSum * createItem.quantity),
                supplierSumIncVat: roundMoneyFormat(props.lot.supplierCostInfo.sumIncVat * createItem.quantity),
                supplierAdprice: props.lot.supplierCostInfo.adprice,

                gosContractFundingSourseName: props.lot.gosContractFundingSourseName,
                retailVat: props.lot.retailCostInfo.vat,
                retailPrice: props.lot.retailCostInfo.price,
                retailVatPrice: props.lot.retailCostInfo.vatPrice,
                retailPriceIncVat: props.lot.retailCostInfo.priceIncVat,
                retailSum: roundMoneyFormat(props.lot.retailCostInfo.sum * createItem.quantity),
                retailVatSum: roundMoneyFormat(props.lot.retailCostInfo.vatSum * createItem.quantity),
                retailSumIncVat: roundMoneyFormat(props.lot.retailCostInfo.sumIncVat * createItem.quantity),
                retailAdprice: props.lot.retailCostInfo.adprice,

                isKiz: props.isWithdrawalGismtForUnscannedLots ? true : props.lot.isKizFlag,
                countKizScan: props.lot.isKiz === false ? undefined : props.kizCount,
                denominator: props.lot.denominator,
                numerator: props.lot.numerator,
                supplier: props.lot.supplier,
                dateCreated: DateTime.now(),
                isOsu: createItem.isOsu,
                bestBefore: props.lot.bestBefore,
                seriesNumber: props.lot.seriesNumber,
                lotNameParent: props.lot.lotName
            },
            filter: {
                idItem: createItem.idGlobal,
                quantityMax: props.lot.quantityRem + props.lot.quantityRes,
                quantityMin: 0,
            },
        };
    };

    const remain = useLotRemain(props.lot.idGlobal, props.idDocumentGlobal);
    const operativeReserve = useOperativeReserve();

    const onOk = () => {
        operativeReserve.send(
            {
                idLot: props.lot.idGlobal,
                idTable: IdTableVariant.Movement,
                idDocument: props.idDocumentGlobal,
                idDocumentItem: props.idDocumentItemGlobal,
                quantity: createItem.quantity,
            },
            () => props.ok(buildDocumentItem())
        );
    };

    const onCancel = () => {
        operativeReserve.send(
            {
                idLot: props.lot.idGlobal,
                idTable: IdTableVariant.Movement,
                idDocument: props.idDocumentGlobal,
                idDocumentItem: props.idDocumentItemGlobal,
                quantity: props.movementItemEdit.quantity,
            },
            props.cancel
        );
    };

    const { isValid, errors, setErrors } = useForm<IValidator>({
        validations: {
            quantity: {
                required: {
                    value: createItem.quantity && createItem.quantity > 0 ? false : true,
                    message: "Значение должно быть больше 0",
                },
            },
        },
    });

    useEffect(() => {
        if (props.requiredQuantity) {
            let calculatedQuantity: number;
            if (props.requiredQuantity > remain) {
                calculatedQuantity = remain;
            } else {
                calculatedQuantity = props.requiredQuantity;
            }
            
            setActualRequiredQuantity(calculatedQuantity);
            
            setCreateItem(prev => ({
                ...prev,
                quantity: calculatedQuantity,
                actualQuantity: props.lot.isKiz ? 0 : calculatedQuantity
            }));
        }
    }, [props.requiredQuantity, remain]);

    useEffect(() => {
        props.isWithdrawalGismtForUnscannedLots && !isDivided && !isBox && !isKiz &&
            goodsDataProvider.getWtruCodeGroup(props.lot.goods.idGlobal,
                (wtruGoodsGroup: IGoodsWtruCodeGroupDTO) => {
                    if (wtruGoodsGroup) {
                        setKizGoodsGroup(wtruGoodsGroup.groupName);
                    }
                },
                () => {
                    setKizGoodsGroup("Не определена");
                }
            );
    }, [props.lot.goods.idGlobal])

    return (
        <BaseModalWindow
            header="Введите количество"
            ok={{
                onClick: () => {
                    if (isValid()) {
                        onOk();
                    }
                },
            }}
            cancel={{ onClick: onCancel }}
            modalWindowClassName={modalStyles.modalWindowMovement}
            footerStyle="confirm"
        >
            <>
                <TextAreaInput value={props.lot.displayName} disabled />
                <GridWrapper cols={3}>
                    <TextInput label="Единица измерения" value={props.lot.scalingRatio.displayName} disabled />
                    <NumberInput label="Остаток" value={remain} disabled />
                    <NumberInput
                        required
                        onFocus={() => setErrors({ ...errors, quantity: undefined })}
                        error={errors.quantity}
                        keyBan={true}
                        label="Количество"
                        value={props.requiredQuantity ? actualRequiredQuantity : createItem.quantity}
                        min={1}
                        max={remain}
                        onChange={(value) => {
                            if (props.requiredQuantity) {
                                const validValue = Math.min(value, remain || value);
                                setActualRequiredQuantity(validValue);
                                setCreateItem({ 
                                    ...createItem, 
                                    quantity: validValue, 
                                });
                            } else {
                                setCreateItem({ 
                                    ...createItem, 
                                    quantity: value, 
                                });
                            }
                        }}
                    />
                </GridWrapper>
                {
                    props.isWithdrawalGismtForUnscannedLots && !isDivided && !isBox && !isKiz &&
                    <TextAreaInput
                        label={baseT("kizGoodsGroup") + ":"}
                        value={kizGoodsGroup}
                        lineTextArea
                        disabled
                    />
                }
            </>
        </BaseModalWindow>
    );
};
